import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { getStatusMeta } from '../../constants/partnerStatuses';
import BorrowerJourney from '../../components/doorlypro/BorrowerJourney';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Partner-facing status palette + copy lives in one place:
// frontend/src/constants/partnerStatuses.js (mirrors backend/utils/partnerStatus.js).

function StatusBadge({ status }) {
  const meta = getStatusMeta(status);
  const s = meta.color;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
      backgroundColor: s.bg, color: s.color,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: s.color }} />
      {meta.label}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '10px 0' }}>
      <div style={{ width: 180, flexShrink: 0, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#111827' }}>{value || '—'}</div>
    </div>
  );
}

export default function DoorlyProReferralDetail() {
  const { id } = useParams();
  // 🚨 THE TYPE IS PASSED TO getStatusMeta NOW. See BorrowerJourney's header:
  // the byPartnerType overrides were selected by nothing, so this page showed
  // a realtor the shared "wait on the buyer" next step for work that is theirs.
  const { token, partner } = useDoorlyProAuth();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token || !id) return;
    axios
      .get(`${API_URL}/api/doorlypro/referrals/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setReferral(res.data?.referral || res.data))
      .catch(() => setError('Failed to load buyer details.'))
      .finally(() => setLoading(false));
  }, [token, id]);

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ maxWidth: 700 }}>
          <div style={{ marginBottom: 24 }}>
            <Link to="/doorlypro/referrals" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
              &larr; Back to My Buyers
            </Link>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>
              Buyer Detail
            </h1>
          </div>

          {error && (
            <div style={{ padding: '12px 16px', borderRadius: 8, backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', color: '#ff082f', fontSize: 13, marginBottom: 20 }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>Loading...</div>
          ) : !referral ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>Buyer not found.</div>
          ) : (
            <>
              {/* Header card */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
                      {referral.first_name} {referral.last_name}
                    </h2>
                    <p style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                      Buyer #{String(referral.id).padStart(4, '0')}
                    </p>
                  </div>
                  <StatusBadge status={referral.partner_status} />
                </div>

                {/* Status explainer — definition + what's next, inline near the badge */}
                {(() => {
                  const meta = getStatusMeta(referral.partner_status, partner?.partner_type);
                  return (
                    <div style={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                      <div style={{ fontSize: 13, color: '#374151', fontWeight: 600, lineHeight: 1.5 }}>{meta.definition}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>
                        <span style={{ fontWeight: 700, color: '#3a6ad9' }}>What's next: </span>{meta.nextStep}
                      </div>
                    </div>
                  );
                })()}

                <div>
                  <DetailRow label="Email" value={referral.email} />
                  <DetailRow label="Phone" value={referral.phone} />
                  {/* ⚠️ THE PARTNER'S OWN TYPED ESTIMATE FROM THE SUBMISSION FORM, NOT A
                      DOORLY FIGURE. Labelled "Est. Purchase Price" it read like an approval
                      amount — which Doorly does not disclose to partners at all. Saying whose
                      number it is costs one word and stops a partner shopping to a figure they
                      invented themselves. See engineeringFollowUps
                      partner-visibility-into-approval-amount for the real question behind it. */}
                  {/* 🚨 THE BUYER'S ACTUAL CEILING, WHEN THEY HAVE SHARED IT — kept
                      directly above the partner's own guess so the two are never
                      confused. Server-resolved and already rounded; null means no
                      figure, whatever the reason. */}
                  {referral.shopping_ceiling && (
                    <DetailRow label="Shopping Range" value={`Up to ${referral.shopping_ceiling}`} />
                  )}
                  {!referral.shopping_ceiling && referral.shopping_ceiling_hint && (
                    <DetailRow label="Shopping Range" value={referral.shopping_ceiling_hint} />
                  )}
                  <DetailRow
                    label="Your Est. Purchase Price"
                    value={referral.purchase_price_estimate ? `$${Number(referral.purchase_price_estimate).toLocaleString()}` : null}
                  />
                  <DetailRow
                    label="Target Close Date"
                    value={referral.target_close_date ? new Date(referral.target_close_date).toLocaleDateString() : null}
                  />
                  <DetailRow
                    label="Submitted"
                    value={referral.created_at ? new Date(referral.created_at).toLocaleString() : null}
                  />
                  <DetailRow label="Notes" value={referral.notes} />
                </div>
              </div>

              {/* Per-buyer status timeline (Borrower Journey) */}
              <BorrowerJourney currentStatus={referral.partner_status} statusHistory={referral.status_history}
                partnerType={partner?.partner_type} />
            </>
          )}
        </div>
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}
